/* Fund-area shared read models (three-tab rebuild, locked 2026-08-31).
 *
 * FUND TABS ARE SYMBOL-FREE (Ale ruling): no legend — plain text tables.
 * The Evidence tab (D6, 2026-09-03) is one row per demand with the
 * object's type dot / trade arrow as its identifier, never a sentence. */

import { apiFetch } from '../../lib/api'
import type { MasterLink } from './masters'
import type { RtSegment } from '../RecordTable'
import type { AsOfNotes, DroppedLeg } from './asOf'

export interface MatrixRow {
  object_type: 'entity' | 'instrument' | 'position' | 'trade'
  object_id: string
  obligation_code: string
  status: 'open' | 'requested' | 'pending_signoff' | 'satisfied' | 'waived'
  is_mandatory: boolean
  /* The demand sits on a plug trade (an officer reconciling movement or
   * an inception balance): an owed row says "on a plug". */
  on_plug?: boolean | null
  /* The DOCUMENT · PAGE · DATED · STATUS detail of the one-row-per-demand
   * Evidence tab (Ale 2026-09-03, D6) and, on a waived row, the waiver's
   * signature (evidence honesty, 2026-09-04). OPTIONAL — an older payload
   * omits them and the tab prints a dash. */
  document_id?: string | null
  document_name?: string | null
  document_kind?: string | null
  page?: number | null
  page_to?: number | null
  dated?: string | null
  valid_to?: string | null
  requested_at?: string | null
  owed_since?: string | null
  waived_by_name?: string | null
  waived_by_role?: string | null
  waived_reason?: string | null
  waived_at?: string | null
}

export interface WalkNode {
  entity_id: string
  entity_name: string
  state: 'open' | 'stopped' | 'by_rule' | 'continues'
  reason_label: string | null
  rule_label: string | null
  papers: { label: string; on_record: boolean }[]
  anomaly: boolean
}

/* `background`: a heartbeat re-fetch, not the officer — never moves the
 * idle clock (lib/api.ts). */
export async function fetchEvidenceMatrix(
  fundId: string,
  background = false
): Promise<MatrixRow[]> {
  const r = await apiFetch<{ rows: MatrixRow[] }>(`/api/funds/${fundId}/evidence-matrix`, {
    background,
  })
  return r.rows
}

export async function fetchWalkStatus(fundId: string, background = false): Promise<WalkNode[]> {
  const r = await apiFetch<{ nodes: WalkNode[] }>(`/api/funds/${fundId}/walk-status`, {
    background,
  })
  return r.nodes
}

/** The master funds this feeder holds (16a/16c/16d). An ordinary fund
 *  returns an empty list. The endpoint is a backend FOLLOW-UP (see
 *  BUILD.md): it must enumerate the fund's issuer entities that are fund
 *  heads (reverse Fund.fund_entity_id lookup) and, per master, return its
 *  own fund id and its own chain-status headline counted IN FULL. Until it
 *  ships, the caller treats a 404 / error as "no masters" and the feeder
 *  view never activates for a real fund. */
export async function fetchMasters(fundId: string, background = false): Promise<MasterLink[]> {
  const r = await apiFetch<{ masters: MasterLink[] }>(`/api/funds/${fundId}/masters`, {
    background,
  })
  return r.masters ?? []
}

/** The demand codes in canonical order and their plain words — the
 *  Evidence tab's DEMAND column ("B1 · holder confirmation"). One
 *  implementation, in the pure row model. */
export {
  DEMAND_ORDER as OBLIGATION_COLUMNS,
  DEMAND_WORDS as OBLIGATION_TITLES,
} from './evidenceRows'

/** Status → hover wording (the cell's why). */
export function statusTitle(status: MatrixRow['status']): string {
  switch (status) {
    case 'satisfied':
      return 'proven'
    case 'pending_signoff':
      return 'document on file — your sign-off open'
    case 'requested':
      return 'requested — the document is owed'
    case 'waived':
      return 'waived, with justification'
    default:
      return 'nothing on file — no request yet'
  }
}

/** The active rebuild lens (Ale ruling 2026-09-03, C8): what the three
 *  tabs and the drawer are showing when the header toggle is off "full
 *  record" — the evidenced record, or the record as of a chosen date. The
 *  label is the caption the surfaces print ("of the evidenced record",
 *  "as of 5 Aug 2026"); the sentences themselves are unchanged — only the
 *  variable they compile over moved. */
export interface Lens {
  mode: 'evidenced' | 'asof'
  label: string
}

/** The record AS OF A DATE (R3b, Ale 2026-09-04) — what the Overview adds
 *  on top of the engine's D view: the holdings the view left out, resolved
 *  to the full record's legs so the diagram greys them IN PLACE; the
 *  reader flags (proof status is as it stands today); the demands of the
 *  full record outside the view's denominator; and the door home. */
export interface AsOfView {
  date: string
  droppedLegs: DroppedLeg<RtSegment>[]
  notes: AsOfNotes
  outside: { count: number; names: string[] }
  onBackToday: () => void
}

/** ev-fraction helper: PROVEN / OWED pill state for a graph box. */
export function provenPill(sat: number, total: number): 'proven' | 'owed' | null {
  if (total <= 0) return null
  return sat >= total ? 'proven' : 'owed'
}
